import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';

import { ProgressCircle, Text } from '@spotify-internal/encore-web';

import styles from './MentionSuggestion.module.css';

export type MentionItem = {
  id: string;
  label: string;
  typeLabel?: string;
  secondaryLabel?: string;
};

export const LOADING_SENTINEL: MentionItem = { id: '__loading__', label: '' };
export const NO_RESULTS_SENTINEL: MentionItem = {
  id: '__no_results__',
  label: '',
};

export type MentionSuggestionKeyDownHandler = (props: {
  event: KeyboardEvent;
}) => boolean;

export type MentionSuggestionProps = {
  items: MentionItem[];
  command: (item: MentionItem) => void;
  keyDownRef?: { current: MentionSuggestionKeyDownHandler | null };
};

export type MentionSuggestionRef = {
  onKeyDown: MentionSuggestionKeyDownHandler;
};

export const MentionSuggestion = forwardRef<
  MentionSuggestionRef,
  MentionSuggestionProps
>(({ items, command, keyDownRef }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLLIElement | null>>([]);

  const isLoading = items.length === 1 && items[0].id === LOADING_SENTINEL.id;
  const isNoResults =
    items.length === 1 && items[0].id === NO_RESULTS_SENTINEL.id;
  const realItems = useMemo(
    () => (isLoading || isNoResults ? [] : items),
    [isLoading, isNoResults, items],
  );

  useEffect(() => {
    setSelectedIndex(0);
    itemRefs.current.length = realItems.length;
    itemRefs.current[0]?.scrollIntoView?.({ block: 'nearest' });
  }, [items, realItems.length]);

  const handleKeyDown = useCallback(
    ({ event }: { event: KeyboardEvent }): boolean => {
      if (event.key === 'ArrowUp') {
        if (realItems.length > 0) {
          setSelectedIndex(prev => {
            const next = (prev + realItems.length - 1) % realItems.length;
            itemRefs.current[next]?.scrollIntoView?.({ block: 'nearest' });
            return next;
          });
        }
        return true;
      }
      if (event.key === 'ArrowDown') {
        if (realItems.length > 0) {
          setSelectedIndex(prev => {
            const next = (prev + 1) % realItems.length;
            itemRefs.current[next]?.scrollIntoView?.({ block: 'nearest' });
            return next;
          });
        }
        return true;
      }
      if (event.key === 'Enter') {
        const item = realItems[selectedIndex];
        if (item) command(item);
        return true;
      }
      return false;
    },
    [realItems, selectedIndex, command],
  );

  useLayoutEffect(() => {
    if (keyDownRef) {
      keyDownRef.current = handleKeyDown;
    }
    return () => {
      if (keyDownRef) {
        keyDownRef.current = null;
      }
    };
  }, [keyDownRef, handleKeyDown]);

  useImperativeHandle(ref, () => ({
    onKeyDown: handleKeyDown,
  }));

  return (
    <div className={styles.root}>
      {isLoading && (
        <div className={styles.statusRow}>
          <ProgressCircle
            aria-label="Loading resource suggestions"
            size="small"
          />
        </div>
      )}
      {isNoResults && (
        <div className={styles.statusRow}>
          <Text variant="bodySmall" semanticColor="textSubdued">
            No suggestions found
          </Text>
        </div>
      )}
      {!isLoading && !isNoResults && (
        <ul
          aria-label="Resource suggestions"
          className={styles.list}
          role="listbox"
        >
          {realItems.map((item, index) => (
            <li
              key={item.id}
              ref={node => {
                itemRefs.current[index] = node;
              }}
              role="option"
              aria-selected={index === selectedIndex}
              className={classNames(styles.item, {
                [styles.itemSelected]: index === selectedIndex,
              })}
              onClick={() => command(item)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  command(item);
                }
              }}
              onMouseEnter={() => setSelectedIndex(index)}
             data-track-source-anchor="3386992aae;mention-suggestion-li">
              <div className={styles.itemContent}>
                {item.typeLabel && (
                  <Text
                    variant="bodySmallBold"
                    semanticColor="textSubdued"
                    className={styles.typeLabel}
                  >
                    {item.typeLabel}:
                  </Text>
                )}
                <Text variant="bodySmall" className={styles.itemLabel}>
                  {item.label}
                </Text>
                {item.secondaryLabel && (
                  <Text
                    variant="marginal"
                    semanticColor="textSubdued"
                    className={styles.secondaryLabel}
                  >
                    {item.secondaryLabel}
                  </Text>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});
MentionSuggestion.displayName = 'MentionSuggestion';
